import React, {
  useState,
  useEffect,
  useContext,
  useId,
  useMemo,
  useRef,
} from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { parsePhoneNumberWithError } from "libphonenumber-js";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/bootstrap.css";
import { City, State, Country } from "country-state-city";
import Select from "react-select";
import LogoutWarning from "./LogoutWarning";
import NotificationContext from "../context/NotificationContext";
import { useMutationLock } from "../hooks/useMutationLock";
import * as usersApi from "../api/users";
import * as publicApi from "../api/publicApi";
import "./UserDetailsModal.css";

const selectClassNames = {
  control: ({ isFocused, isDisabled }) =>
    [
      "user-details-select__control",
      isFocused ? "user-details-select__control--is-focused" : "",
      isDisabled ? "user-details-select__control--is-disabled" : "",
    ]
      .filter(Boolean)
      .join(" "),
  valueContainer: () => "user-details-select__value-container",
  placeholder: () => "user-details-select__placeholder",
  singleValue: () => "user-details-select__single-value",
  indicatorSeparator: () => "user-details-select__indicator-separator",
  menu: () => "user-details-select__menu",
  option: ({ isFocused, isSelected }) =>
    [
      "user-details-select__option",
      isFocused ? "user-details-select__option--is-focused" : "",
      isSelected ? "user-details-select__option--is-selected" : "",
    ]
      .filter(Boolean)
      .join(" "),
};

function isTextFilled(value) {
  return typeof value === "string" && value.trim().length > 0;
}

function fieldStatusClass(done) {
  return done ? " is-done" : "";
}

function TextField({
  id,
  label,
  hint,
  value,
  onChange,
  required = false,
  className = "",
  placeholder,
  filled,
}) {
  const isDone = filled ?? isTextFilled(value);
  return (
    <div
      className={`user-details-field${fieldStatusClass(isDone)} ${className}`.trim()}
    >
      <label className="user-details-field__label" htmlFor={id}>
        {label}
      </label>
      {hint ? <p className="user-details-field__hint">{hint}</p> : null}
      <input
        id={id}
        type="text"
        className="user-details-input"
        value={value}
        onChange={onChange}
        required={required}
        placeholder={placeholder}
      />
    </div>
  );
}

const UserDetailsModal = ({ userEmail, onDetailsSaved }) => {
  const [email] = useState(userEmail || "");
  const [firstname, setFirstName] = useState("");
  const [lastname, setLastName] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [parentsPhoneNumber, setParentsPhoneNumber] = useState("");
  const [address, setAddress] = useState("");
  const [school, setSchool] = useState("");
  const [grade, setGrade] = useState("");
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [showlogout, setShowLogout] = useState(false);
  const notificationCtx = useContext(NotificationContext);
  const { busy, run } = useMutationLock();
  const { logout } = useAuth0();
  const titleId = useId();
  const closeBtnRef = useRef(null);

  const [selectedCountry, setSelectedCountry] = useState(null);
  const [selectedState, setSelectedState] = useState(null);
  const [selectedCity, setSelectedCity] = useState(null);
  const [stateOptions, setStateOptions] = useState([]);
  const [cityOptions, setCityOptions] = useState([]);
  const [grades, setGrades] = useState([]);

  const countryOptions = useMemo(
    () =>
      Country.getAllCountries().map((country) => ({
        label: `${country.flag} ${country.name}`,
        value: country.isoCode,
      })),
    []
  );

  const availableGrades = useMemo(
    () =>
      grades.map((s) => ({
        label: s.name,
        value: s.name,
      })),
    [grades]
  );

  const validatePhone = (number) => {
    try {
      const phone = parsePhoneNumberWithError("+" + number);
      return phone?.isValid() || false;
    } catch {
      return false;
    }
  };

  const phoneValid = validatePhone(phoneNumber);
  const parentPhoneValid = validatePhone(parentsPhoneNumber);
  const phonesDifferent =
    phoneNumber.length > 0 &&
    parentsPhoneNumber.length > 0 &&
    phoneNumber !== parentsPhoneNumber;

  const handleCountryChange = (countryOption) => {
    setSelectedCountry(countryOption);
    setSelectedState(null);
    setSelectedCity(null);

    const states = State.getStatesOfCountry(countryOption.value);
    setStateOptions(
      states.map((state) => ({
        label: state.name,
        value: state.isoCode,
      }))
    );
    setCityOptions([]);
  };

  const handleStateChange = (stateOption) => {
    setSelectedState(stateOption);
    setSelectedCity(null);

    const cities = City.getCitiesOfState(
      selectedCountry.value,
      stateOption.value
    );
    setCityOptions(
      cities.map((city) => ({
        label: city.name,
        value: city.name,
      }))
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormSubmitted(true);

    const isValidUser = validatePhone(phoneNumber);
    const isValidParent = validatePhone(parentsPhoneNumber);
    const isSame = phoneNumber === parentsPhoneNumber;
    const locationReady =
      selectedCountry && selectedState && selectedCity && address.trim();

    if (!isValidUser || !isValidParent || isSame || !locationReady) return;

    await run(async () => {
      try {
        const formData = {
          email,
          name: firstname + " " + lastname,
          phoneNumber: "+" + phoneNumber,
          parentsPhoneNumber: "+" + parentsPhoneNumber,
          address,
          city: selectedCity.label,
          province: selectedState.label,
          country: selectedCountry.label,
          school,
          grade,
        };
        await usersApi.saveDetails(formData);
        notificationCtx.show({
          message: "User details saved successfully!",
          onConfirm: () => onDetailsSaved(),
          stat: "success",
        });
      } catch (error) {
        console.error("Error saving user details:", error);
        notificationCtx.show({
          message: "Failed to save user details. Please try again.",
          stat: "error",
        });
      }
    });
  };

  useEffect(() => {
    const fetchGrades = async () => {
      try {
        const response = await publicApi.getGrades();
        setGrades(response.data);
      } catch (err) {
        console.error("Error fetching grades:", err);
      }
    };

    fetchGrades();
  }, []);

  useEffect(() => {
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const onKeyDown = (event) => {
      if (event.key === "Escape") {
        setShowLogout(true);
      }
    };

    window.addEventListener("keydown", onKeyDown);
    closeBtnRef.current?.focus();

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", onKeyDown);
    };
  }, []);

  return (
    <>
      <div className="user-details-overlay" role="presentation">
        <div
          className="user-details-dialog"
          role="dialog"
          aria-modal="true"
          aria-labelledby={titleId}
        >
          <div className="user-details-dialog__top">
            <div>
              <p className="user-details-dialog__eyebrow">Glad you&apos;re here</p>
              <h2 id={titleId} className="user-details-dialog__title">
                Let&apos;s get you set up
              </h2>
              <p className="user-details-dialog__lead">
                A few details help us create your student account and keep in
                touch. It only takes a minute, then you&apos;re ready to explore.
              </p>
            </div>
            <button
              ref={closeBtnRef}
              type="button"
              className="user-details-dialog__close"
              onClick={() => setShowLogout(true)}
              disabled={busy}
              aria-label="Leave for now"
            >
              ×
            </button>
          </div>

          <form
            onSubmit={handleSubmit}
            className="user-details-form"
            noValidate
          >
            <div className="user-details-form__scroll">
              <div className="user-details-block">
                <div className="user-details-block__head">
                  <h3 className="user-details-block__title">About you</h3>
                  <p className="user-details-email">
                    <span className="user-details-email__label">Email</span>
                    <span className="user-details-email__value">{email}</span>
                  </p>
                </div>
                <div className="user-details-grid">
                  <TextField
                    id="ud-first-name"
                    label="First Name"
                    value={firstname}
                    onChange={(e) => setFirstName(e.target.value)}
                    required
                  />
                  <TextField
                    id="ud-last-name"
                    label="Last Name"
                    value={lastname}
                    onChange={(e) => setLastName(e.target.value)}
                    required
                  />
                </div>
              </div>

              <div className="user-details-block">
                <h3 className="user-details-block__title">How we reach you</h3>
                <div className="user-details-grid">
                  <div
                    className={`user-details-field${fieldStatusClass(
                      phoneValid
                    )}`}
                  >
                    <label
                      className="user-details-field__label"
                      htmlFor="ud-phone"
                    >
                      Phone Number
                    </label>
                    <p className="user-details-field__hint">
                      A WhatsApp number works best. That&apos;s how we share
                      updates and class reminders with you.
                    </p>
                    <div className="user-details-phone">
                      <PhoneInput
                        country={"pk"}
                        value={phoneNumber}
                        onChange={setPhoneNumber}
                        enableSearch
                        countryCodeEditable={false}
                        inputProps={{ id: "ud-phone" }}
                        placeholder="Enter number"
                      />
                    </div>
                    {formSubmitted && !phoneValid && (
                      <p className="user-details-field__error">
                        Please enter a valid phone number.
                      </p>
                    )}
                  </div>

                  <div
                    className={`user-details-field${fieldStatusClass(
                      parentPhoneValid && phonesDifferent
                    )}`}
                  >
                    <label
                      className="user-details-field__label"
                      htmlFor="ud-parent-phone"
                    >
                      Parent&apos;s Phone Number
                    </label>
                    <p className="user-details-field__hint">
                      A parent or guardian number helps us reach someone if we
                      ever need a second way to get in touch.
                    </p>
                    <div className="user-details-phone">
                      <PhoneInput
                        country={"pk"}
                        value={parentsPhoneNumber}
                        onChange={setParentsPhoneNumber}
                        enableSearch
                        countryCodeEditable={false}
                        inputProps={{ id: "ud-parent-phone" }}
                        placeholder="Enter number"
                      />
                    </div>
                    {formSubmitted && !parentPhoneValid && (
                      <p className="user-details-field__error">
                        Please enter a valid phone number.
                      </p>
                    )}
                  </div>

                  {formSubmitted &&
                    phoneValid &&
                    parentPhoneValid &&
                    !phonesDifferent && (
                      <div className="user-details-field user-details-field--span2">
                        <p className="user-details-field__error">
                          User and parent numbers must be different.
                        </p>
                      </div>
                    )}
                </div>
              </div>

              <div className="user-details-block">
                <h3 className="user-details-block__title">Where you are</h3>
                <p className="user-details-block__hint">
                  Start with your country. State and city will open up as you
                  go.
                </p>

                <div className="user-details-location">
                  <div
                    className={`user-details-location__step${
                      selectedCountry ? " is-done" : " is-current"
                    }`}
                  >
                    <div className="user-details-location__meta">
                      <span className="user-details-location__index" aria-hidden="true">
                        1
                      </span>
                      <label
                        className="user-details-field__label"
                        htmlFor="ud-country"
                      >
                        Country
                      </label>
                    </div>
                    <Select
                      inputId="ud-country"
                      options={countryOptions}
                      value={selectedCountry}
                      onChange={handleCountryChange}
                      placeholder="Select your country"
                      isSearchable
                      classNamePrefix="user-details-select"
                      classNames={selectClassNames}
                    />
                  </div>

                  <div
                    className="user-details-location__connector"
                    aria-hidden="true"
                  />

                  <div
                    className={`user-details-location__step${
                      !selectedCountry
                        ? " is-locked"
                        : selectedState
                          ? " is-done"
                          : " is-current"
                    }`}
                  >
                    <div className="user-details-location__meta">
                      <span className="user-details-location__index" aria-hidden="true">
                        2
                      </span>
                      <label
                        className="user-details-field__label"
                        htmlFor="ud-state"
                      >
                        State / Province
                      </label>
                    </div>
                    <Select
                      inputId="ud-state"
                      options={stateOptions}
                      value={selectedState}
                      onChange={handleStateChange}
                      placeholder={
                        selectedCountry
                          ? "Select your state"
                          : "Choose a country first"
                      }
                      isSearchable
                      isDisabled={!selectedCountry}
                      classNamePrefix="user-details-select"
                      classNames={selectClassNames}
                    />
                  </div>

                  <div
                    className="user-details-location__connector"
                    aria-hidden="true"
                  />

                  <div
                    className={`user-details-location__step${
                      !selectedState
                        ? " is-locked"
                        : selectedCity
                          ? " is-done"
                          : " is-current"
                    }`}
                  >
                    <div className="user-details-location__meta">
                      <span className="user-details-location__index" aria-hidden="true">
                        3
                      </span>
                      <label
                        className="user-details-field__label"
                        htmlFor="ud-city"
                      >
                        City
                      </label>
                    </div>
                    <Select
                      inputId="ud-city"
                      options={cityOptions}
                      value={selectedCity}
                      onChange={setSelectedCity}
                      placeholder={
                        selectedState
                          ? "Select your city"
                          : selectedCountry
                            ? "Choose a state next"
                            : "Choose a country first"
                      }
                      isSearchable
                      isDisabled={!selectedState}
                      classNamePrefix="user-details-select"
                      classNames={selectClassNames}
                    />
                  </div>
                </div>

                <div
                  className={`user-details-field user-details-field--address${fieldStatusClass(
                    isTextFilled(address)
                  )}`}
                >
                  <label
                    className="user-details-field__label"
                    htmlFor="ud-address"
                  >
                    Address
                  </label>
                  <p className="user-details-field__hint">
                    We dispatch merchandise and gifts for students, so please
                    fill in an accurate address.
                  </p>
                  <input
                    id="ud-address"
                    type="text"
                    className="user-details-input"
                    value={address}
                    onChange={(e) => setAddress(e.target.value)}
                    required
                  />
                </div>

                {formSubmitted &&
                  (!selectedCountry ||
                    !selectedState ||
                    !selectedCity ||
                    !address.trim()) && (
                    <p className="user-details-field__error">
                      Please complete country, state, city, and address.
                    </p>
                  )}
              </div>

              <div className="user-details-block">
                <h3 className="user-details-block__title">Your studies</h3>
                <div className="user-details-grid">
                  <TextField
                    id="ud-school"
                    label="School"
                    hint="Where are you studying these days?"
                    value={school}
                    onChange={(e) => setSchool(e.target.value)}
                    required
                    placeholder="Enter school name"
                  />
                  <div
                    className={`user-details-field${fieldStatusClass(
                      isTextFilled(grade)
                    )}`}
                  >
                    <label
                      className="user-details-field__label"
                      htmlFor="ud-grade"
                    >
                      Grade
                    </label>
                    <p className="user-details-field__hint">
                      Which grade are you in right now?
                    </p>
                    <select
                      id="ud-grade"
                      name="grade"
                      required
                      className="user-details-input"
                      value={grade}
                      onChange={(e) => setGrade(e.target.value)}
                    >
                      <option value="" disabled>
                        Select Grade
                      </option>
                      {availableGrades.map((gradeOption) => (
                        <option
                          key={gradeOption.value}
                          value={gradeOption.value}
                        >
                          {gradeOption.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
            </div>

            <div className="user-details-footer">
              <p className="user-details-footer__note">
                Prefer to come back later? Leaving for now will sign you out.
                You can pick this up anytime you return.
              </p>
              <div className="user-details-footer__nav">
                <button
                  type="button"
                  className="user-details-btn user-details-btn--ghost"
                  disabled={busy}
                  onClick={() => setShowLogout(true)}
                >
                  Leave for now
                </button>
                <button
                  type="submit"
                  className="user-details-btn user-details-btn--primary"
                  disabled={busy}
                >
                  {busy ? "Saving..." : "Save & continue"}
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>

      {showlogout && (
        <LogoutWarning
          onClose={() => setShowLogout(false)}
          onLogout={() => {
            setShowLogout(false);
            logout();
            window.location.href = "/";
          }}
        />
      )}
    </>
  );
};

export default UserDetailsModal;
